import {
  ChildMixUpdate,
  DeploymentType,
  generatedApi,
  LayerSource,
  LoadBasketRead,
  useUpdateAndDeployPlanResultsMutation,
} from "src/store/api/generatedApi";

import { PlanId } from "components/common/boundary/PlanId";
import { Period } from "hooks/periodIndex";
import { steelGradeSearchSlice } from "src/store/slices/steelGradeSearchSlice";
import { AppDispatch } from "src/store/store";
import { getBasketClientStatus } from "../utils";
import { CoreDeploymentType } from "components/common/plan/types";

type InitialDeploymentType = "standard" | "backup";

export const initialDeploymentTypeToDeploymentTypes: Record<
  InitialDeploymentType,
  DeploymentType[]
> = {
  standard: ["standard", "edited_standard"],
  backup: ["backup", "edited_backup"],
};

export const handleOnSubmit =
  (
    dispatch: AppDispatch,
    tenantName: string,
    startAt: Date,
    endAt: Date,
    planId: PlanId,
    period: Period,
    deploymentType: CoreDeploymentType,
    mutation: ReturnType<typeof useUpdateAndDeployPlanResultsMutation>[0]
  ) =>
  async (
    updates: ChildMixUpdate[],
    comment: string,
    layerSource: LayerSource
  ): Promise<void> => {
    const mutationDeploymentType = (() => {
      switch (deploymentType) {
        case "backup":
        case "edited_backup":
          return "edited_backup";
        case "standard":
        case "edited_standard":
          return "edited_standard";
      }
    })();

    const { data } = await mutation({
      tenantName,
      planId,
      deployedPlanMixUpdates: {
        period_updates: [{ period: period, updates, replacements: {} }],
        comment: comment === "" ? null : comment,
        layer_source: layerSource,
        start_at: startAt.toISOString(),
        end_at: endAt.toISOString(),
        deployment_type: mutationDeploymentType,
      },
    });

    if (data) {
      const updateDeploymentTypes = (() => {
        switch (deploymentType) {
          case "backup":
          case "edited_backup":
            return initialDeploymentTypeToDeploymentTypes.backup;
          case "standard":
          case "edited_standard":
            return initialDeploymentTypeToDeploymentTypes.standard;
        }
      })();
      dispatch(
        generatedApi.util.updateQueryData(
          "getDeployedPlanResults",
          {
            tenantName,
            planId: "latest",
            deploymentTypes: updateDeploymentTypes,
            period: period,
          },
          () => data
        )
      );
      dispatch(
        steelGradeSearchSlice.actions.copy([
          planId,
          period,
          "supervisor",
          data.plan_id as PlanId,
          period,
        ])
      );
      dispatch(generatedApi.util.invalidateTags(["deployments"]));
    }
  };

export const mapBasketInfos = (baskets: Record<string, LoadBasketRead>) => {
  return Object.values(baskets)
    .map((basket) => ({
      basketNumber: basket.basket_number,
      loadBasketId: basket.load_basket_id,
      status: basket.status,
      assignedTo:
        basket.assigned_to_uid && basket.assigned_to_name
          ? {
              name: basket.assigned_to_name,
              uid: basket.assigned_to_uid,
            }
          : null,
      clientStatus: getBasketClientStatus({
        basketNumber: basket.basket_number,
        loadBasketId: basket.load_basket_id,
        status: basket.status,
        assignedTo:
          basket.assigned_to_uid && basket.assigned_to_name
            ? {
                name: basket.assigned_to_name,
                uid: basket.assigned_to_uid,
              }
            : null,
      }),
    }))
    .sort((a, b) => a.basketNumber - b.basketNumber);
};
